import React, { useCallback, useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence, PanInfo } from 'framer-motion';
import { ChevronLeft, ChevronRight } from 'lucide-react';
const AUTOPLAY_INTERVAL = 7500;
const swipeConfidenceThreshold = 10000;
const swipePower = (offset: number, velocity: number) => Math.abs(offset) * velocity;
interface ProofCarouselProps {
  language: 'en' | 'es';
}
export function ProofCarousel({
  language
}: ProofCarouselProps) {
  const t = {
    en: {
      eyebrow: 'PROOF FROM THE FIELD',
      heading: 'REAL EQUIPMENT. REAL WEST TEXAS WORK.',
      ctaText: 'Need availability fast? Call or text us today.',
      ctaBtn: 'GET A QUOTE →',
      slides: [{
        headline: 'BUILT FOR OILFIELD. READY FOR ANYTHING.',
        subtext: 'Pipeline crews, construction sites, events, and ranches across West Texas.'
      }, {
        headline: 'WE HANDLE THE DIRTY WORK.',
        subtext: 'Our own service truck keeps every unit clean — before and during your rental.'
      }, {
        headline: 'ONE TRAILER. TWO SOLUTIONS.',
        subtext: 'Restrooms and waste collection combined — built for your job site.'
      }, {
        headline: 'ON-SITE SERVICING. ON SCHEDULE.',
        subtext: 'We come to you. Regular cleaning and maintenance at your location.'
      }, {
        headline: 'HDPE PIPE FUSION. OILFIELD READY.',
        subtext: 'McElroy machine rental for pipeline work across West Texas.'
      }, {
        headline: 'OILFIELD READY. FIELD TESTED.',
        subtext: 'Portable restrooms deployed across West Texas job sites and ranches.'
      }, {
        headline: 'TRAILER MOUNTED. READY TO ROLL.',
        subtext: 'Portable restrooms on trailer for easy deployment anywhere in West Texas.'
      }, {
        headline: 'WASTE MANAGEMENT HANDLED.',
        subtext: 'Trash trailers for oilfield, construction, and large events across West Texas.'
      }, {
        headline: 'KEEP YOUR SITE CLEAN.',
        subtext: 'Open trash trailers available for job sites, ranches, and city events.'
      }]
    },
    es: {
      eyebrow: 'PRUEBA DEL CAMPO',
      heading: 'EQUIPO REAL. TRABAJO REAL DE WEST TEXAS.',
      ctaText: '¿Necesitas disponibilidad rápida? Llámanos o escríbenos hoy.',
      ctaBtn: 'OBTÉN COTIZACIÓN →',
      slides: [{
        headline: 'HECHO PARA EL OILFIELD. LISTO PARA TODO.',
        subtext: 'Cuadrillas de tubería, obras, eventos y ranchos en todo West Texas.'
      }, {
        headline: 'NOSOTROS NOS ENCARGAMOS DEL TRABAJO SUCIO.',
        subtext: 'Nuestro propio camión de servicio mantiene cada unidad limpia — antes y durante tu renta.'
      }, {
        headline: 'UN REMOLQUE. DOS SOLUCIONES.',
        subtext: 'Baños y recolección de basura combinados para tu sitio de trabajo.'
      }, {
        headline: 'SERVICIO EN SITIO. EN HORARIO.',
        subtext: 'Vamos a ti. Limpieza y mantenimiento regular en tu ubicación.'
      }, {
        headline: 'FUSIÓN DE TUBERÍA HDPE. LISTA PARA OILFIELD.',
        subtext: 'Renta de máquina McElroy para trabajo de tubería en West Texas.'
      }, {
        headline: 'LISTOS PARA EL OILFIELD.',
        subtext: 'Baños portátiles desplegados en obras y ranchos de West Texas.'
      }, {
        headline: 'MONTADO EN REMOLQUE. LISTO PARA MOVERSE.',
        subtext: 'Baños portátiles en remolque para despliegue fácil en todo West Texas.'
      }, {
        headline: 'MANEJO DE RESIDUOS RESUELTO.',
        subtext: 'Remolques de basura para oilfield, construcción y eventos en West Texas.'
      }, {
        headline: 'MANTÉN TU SITIO LIMPIO.',
        subtext: 'Remolques abiertos disponibles para obras, ranchos y eventos de ciudad.'
      }]
    }
  };
  const text = t[language];
  const slideImages = ['https://cdn.magicpatterns.com/uploads/mvnQry2poHiSYarSNmutBa/slide_1.png', 'https://cdn.magicpatterns.com/uploads/eCTAG4btAszV4nEWQNnrxa/slide_2_(2).png', 'https://cdn.magicpatterns.com/uploads/275veWcyrTguhr9b5mDvcD/slide_3.png', 'https://cdn.magicpatterns.com/uploads/jk8DqQ9a1fyrPHzdPwm3rG/slide_5.png', 'https://cdn.magicpatterns.com/uploads/m33Go8kgjL7fvYzgTg7Gy9/weldiadora.png', 'https://cdn.magicpatterns.com/uploads/oDjppyQAFZvPDukLHBLcY4/erv1.png', 'https://cdn.magicpatterns.com/uploads/sNaQNM2mDiGSgC7SF5aM4p/erv2.png', 'https://cdn.magicpatterns.com/uploads/iREYbKBNUus91wne56BdVR/slide_6.png', 'https://cdn.magicpatterns.com/uploads/fD4MXU2AV6s8kud8wYawPo/erv3.png'];
  const [[current, direction], setCurrent] = useState([0, 0]);
  const [isPaused, setIsPaused] = useState(false);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const paginate = useCallback((newDirection: number) => {
    setCurrent(prev => {
      const [cur] = prev;
      const nextIndex = cur + newDirection;
      if (nextIndex < 0) return [slideImages.length - 1, newDirection];
      if (nextIndex >= slideImages.length) return [0, newDirection];
      return [nextIndex, newDirection];
    });
  }, []);
  useEffect(() => {
    if (isPaused) {
      if (timerRef.current) clearInterval(timerRef.current);
      return;
    }
    timerRef.current = setInterval(() => {
      paginate(1);
    }, AUTOPLAY_INTERVAL);
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [isPaused, paginate]);
  const resetAutoplay = useCallback(() => {
    if (timerRef.current) clearInterval(timerRef.current);
    if (!isPaused) {
      timerRef.current = setInterval(() => {
        paginate(1);
      }, AUTOPLAY_INTERVAL);
    }
  }, [isPaused, paginate]);
  const goTo = useCallback((index: number) => {
    setCurrent(prev => [index, index > prev[0] ? 1 : -1]);
    resetAutoplay();
  }, [resetAutoplay]);
  const handlePaginate = useCallback((dir: number) => {
    paginate(dir);
    resetAutoplay();
  }, [paginate, resetAutoplay]);
  const handleDragEnd = (_: unknown, info: PanInfo) => {
    const swipe = swipePower(info.offset.x, info.velocity.x);
    if (swipe < -swipeConfidenceThreshold) {
      handlePaginate(1);
    } else if (swipe > swipeConfidenceThreshold) {
      handlePaginate(-1);
    }
  };
  const variants = {
    enter: (dir: number) => ({
      x: dir > 0 ? '100%' : '-100%',
      opacity: 0
    }),
    center: {
      x: 0,
      opacity: 1
    },
    exit: (dir: number) => ({
      x: dir < 0 ? '100%' : '-100%',
      opacity: 0
    })
  };
  const slide = text.slides[current];
  return <section className="py-10 md:py-16 bg-obsidian border-t border-steel w-full overflow-hidden" data-id="element-222">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-223">
        {/* Section Header */}
        <div className="text-center mb-4 md:mb-10 px-4 md:px-0" data-id="element-224">
          <span className="font-montserrat text-gold text-[11px] md:text-xs font-bold tracking-[2px] md:tracking-[3px] uppercase mb-2 md:mb-3 block" data-id="element-225">
            {text.eyebrow}
          </span>
          <h2 className="font-oswald font-bold text-warmWhite/80 uppercase tracking-wide" style={{
          fontSize: 'clamp(1.3rem, 5vw, 1.5rem)'
        }} data-id="element-226">
            {text.heading}
          </h2>
        </div>

        {/* Carousel */}
        <div className="relative max-w-5xl mx-auto" onMouseEnter={() => setIsPaused(true)} onMouseLeave={() => setIsPaused(false)} data-id="element-227">
          <div className="rounded-lg overflow-hidden bg-carbon border border-steel" data-id="element-228">
            {/* Image container */}
            <div className="relative h-[220px] md:h-auto md:aspect-[21/9] overflow-hidden" data-id="element-229">
              <AnimatePresence initial={false} custom={direction} mode="wait" data-id="element-230">
                <motion.div key={current} custom={direction} variants={variants} initial="enter" animate="center" exit="exit" transition={{
                duration: 0.5,
                ease: 'easeInOut'
              }} drag="x" dragConstraints={{
                left: 0,
                right: 0
              }} dragElastic={1} onDragEnd={handleDragEnd} className="absolute inset-0 cursor-grab active:cursor-grabbing" data-id="element-231">
                  <div className="absolute inset-0 bg-cover bg-center" style={{
                  backgroundImage: `url("${slideImages[current]}")`
                }} data-id="element-232" />
                  {/* Desktop text overlay */}
                  <div className="hidden md:block absolute bottom-0 left-0 right-0 z-10 bg-black/75 backdrop-blur-sm px-8 md:px-10 py-3 sm:py-4" data-id="element-233">
                    <h3 className="font-oswald text-sm md:text-base font-bold text-warmWhite uppercase" style={{
                    textShadow: '0 1px 6px rgba(0,0,0,0.5)'
                  }} data-id="element-234">
                      {slide.headline}
                    </h3>
                    <p className="font-inter text-gray-400 text-sm mt-0.5" data-id="element-235">
                      {slide.subtext}
                    </p>
                  </div>
                </motion.div>
              </AnimatePresence>

              {/* Arrow Controls */}
              <button onClick={() => handlePaginate(-1)} className="absolute left-3 sm:left-4 top-1/2 -translate-y-1/2 z-20 w-9 h-9 sm:w-11 sm:h-11 rounded-full bg-obsidian/60 border border-gold/30 flex items-center justify-center text-gold hover:bg-gold hover:text-obsidian transition-all duration-300 backdrop-blur-sm" aria-label="Previous slide" data-id="element-236">
                <ChevronLeft size={20} data-id="element-237" />
              </button>
              <button onClick={() => handlePaginate(1)} className="absolute right-3 sm:right-4 top-1/2 -translate-y-1/2 z-20 w-9 h-9 sm:w-11 sm:h-11 rounded-full bg-obsidian/60 border border-gold/30 flex items-center justify-center text-gold hover:bg-gold hover:text-obsidian transition-all duration-300 backdrop-blur-sm" aria-label="Next slide" data-id="element-238">
                <ChevronRight size={20} data-id="element-239" />
              </button>

              {/* Progress bar */}
              <div className="absolute bottom-0 left-0 right-0 z-30 h-0.5 bg-black/30" data-id="element-240">
                <motion.div key={`progress-${current}-${isPaused}`} className="h-full bg-gold/70" initial={{
                width: '0%'
              }} animate={{
                width: isPaused ? undefined : '100%'
              }} transition={{
                duration: isPaused ? 0 : AUTOPLAY_INTERVAL / 1000,
                ease: 'linear'
              }} data-id="element-241" />
              </div>
            </div>

            {/* Mobile text below image */}
            <div className="md:hidden bg-carbon px-4 py-3" data-id="element-242">
              <h3 className="font-oswald text-base font-extrabold text-warmWhite uppercase leading-tight mb-1.5" data-id="element-243">
                {slide.headline}
              </h3>
              <p className="font-inter text-warmWhite/75 text-[0.85rem] leading-relaxed" data-id="element-244">
                {slide.subtext}
              </p>
            </div>
          </div>

          <div className="flex items-center justify-center gap-2.5 mt-5" data-id="element-245">
            {slideImages.map((_, index) => <button key={index} onClick={() => goTo(index)} className={`rounded-full transition-all duration-300 ${index === current ? 'w-7 h-2 bg-gold' : 'w-2 h-2 bg-steel hover:bg-gold/50'}`} aria-label={`Go to slide ${index + 1}`} data-id="element-246" />)}
          </div>
        </div>

        <div className="text-center mt-6 md:mt-10" data-id="element-247">
          <p className="font-inter text-gray-400 text-sm mb-4" data-id="element-248">
            {text.ctaText}
          </p>
          <a href="#quote" className="inline-flex items-center justify-center gap-2 bg-gold text-obsidian px-10 py-4 rounded font-montserrat font-extrabold text-[16px] tracking-wide hover:bg-yellow-500 hover:scale-105 transition-all duration-300 shadow-lg shadow-gold/20" data-id="element-249">
            {text.ctaBtn}
          </a>
        </div>
      </div>
    </section>;
}